import { forwardRef, useCallback, useMemo, useState } from "react";
import styled from "styled-components";

/** Recorta bordas arredondadas; o scroll fica no filho interno */
const OuterShell = styled.div`
  box-sizing: border-box;
  border: 1px solid rgba(0, 0, 0, 0.15);
  width: 100%;
  height: 100%;
  min-height: 0;
  flex: 1;
  background-color: #f3f3f3;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow: auto;
  scrollbar-width: thin;
  scrollbar-color: #b0b0b0 transparent;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #b0b0b0;
    border-radius: 8px;
    min-height: 48px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: #8f8f8f;
  }

  &::-webkit-scrollbar-corner {
    background-color: transparent;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  color: #333;

  thead {
    background-color: #f0f0f0;
    position: sticky;
    top: 0;
    z-index: 2;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  }

  thead tr {
    height: 40px;
    border-bottom: 1px solid #ccc;
  }

  tbody tr {
    height: 35px;
    border-bottom: 1px solid #e1e1e1;
  }

  tbody tr:nth-child(even) {
    background-color: #f9f9f9;
  }

  tbody tr:nth-child(odd) {
    background-color: #ffffff;
  }

  tbody tr:hover {
    background-color: #e8f0fe;
  }
`;

const Th = styled.th`
  text-align: ${(p) => p.$align ?? "left"};
  padding: 0 12px;
  font-weight: 700;
  white-space: nowrap;
  color: #242d34;
  font-size: 13px;
  width: ${(p) => p.$width ?? "auto"};
`;

const SortButton = styled.button`
  all: unset;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
  padding: 8px 0;

  &:hover {
    color: #007bff;
  }

  &:focus-visible {
    outline: 2px solid #007bff;
    outline-offset: 2px;
    border-radius: 6px;
  }
`;

const SortIcon = styled.span`
  font-size: 12px;
  opacity: ${(p) => (p.$active ? 1 : 0.35)};
`;

const Td = styled.td`
  text-align: ${(p) => p.$align ?? "left"};
  padding: 0 12px;
  padding-right: 10px;
  white-space: nowrap;
  width: ${(p) => p.$width ?? "auto"};
`;

const EmptyCell = styled.td`
  padding: 18px 12px;
  text-align: center;
  color: #333;
  opacity: 0.8;
`;

const defaultGetRowId = (row, idx) => row?.id ?? row?._id ?? row?.key ?? idx;

function getByAccessor(row, accessor) {
  if (typeof accessor === "function") return accessor(row);
  if (!accessor) return undefined;
  if (typeof accessor !== "string") return undefined;

  // suporte simples a "A.B.C"
  return accessor.split(".").reduce((acc, key) => (acc == null ? acc : acc[key]), row);
}

function normalizeForSort(value) {
  if (value == null) return { kind: "null", value: null };
  if (typeof value === "number") return { kind: "number", value };
  if (value instanceof Date) return { kind: "number", value: value.getTime() };
  if (typeof value === "boolean") return { kind: "number", value: value ? 1 : 0 };

  const asNumber = Number(value);
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(asNumber)) {
    return { kind: "number", value: asNumber };
  }

  return { kind: "string", value: String(value) };
}

const collator = new Intl.Collator("pt-BR", { numeric: true, sensitivity: "base" });
const stringCollator = new Intl.Collator("pt-BR", { numeric: false, sensitivity: "base" });

/**
 * SortableTable
 *
 * columns: [
 *  { id: 'name', header: 'NOME', accessor: (row) => row.name, sortable: true, width, align, render, sortValue, sortType }
 *
 * sortType: "string" força comparação lexicográfica (sem coerção numérica),
 * útil para códigos hierárquicos como contas contábeis ("1" < "11" < "2").
 * ]
 */
const SortableTable = forwardRef(
  (
    {
      columns,
      data,
      getRowId = defaultGetRowId,
      onRowClick,
      rowCursor = "default",
      emptyText = "NENHUM REGISTRO ENCONTRADO",
      initialSort,
      limit,
      isLoading = false,
      loadingText = "Carregando...",
      getRowProps,
      onScrollReachEnd,
      scrollReachThreshold = 40,
    },
    ref
  ) => {
    const [sort, setSort] = useState(() => {
      if (initialSort?.key) return { key: initialSort.key, direction: initialSort.direction ?? "asc" };
      return { key: null, direction: "asc" };
    });

    const rows = useMemo(() => {
      const input = Array.isArray(data) ? data : [];
      const sortableCols = Array.isArray(columns) ? columns : [];

      if (!sort.key) return input;
      const col = sortableCols.find((c) => c.id === sort.key);
      if (!col || col.sortable === false) return input;

      const dir = sort.direction === "desc" ? -1 : 1;

      const sorted = [...input].sort((a, b) => {
        const av = col.sortValue ? col.sortValue(a) : getByAccessor(a, col.accessor);
        const bv = col.sortValue ? col.sortValue(b) : getByAccessor(b, col.accessor);

        // Ordenação lexicográfica pura (ex.: códigos de conta contábil "1" < "11" < "2")
        if (col.sortType === "string") {
          const sa = av == null ? "" : String(av);
          const sb = bv == null ? "" : String(bv);
          if (sa === "" && sb === "") return 0;
          if (sa === "") return 1;
          if (sb === "") return -1;
          return stringCollator.compare(sa, sb) * dir;
        }

        const na = normalizeForSort(av);
        const nb = normalizeForSort(bv);

        if (na.kind === "null" && nb.kind === "null") return 0;
        if (na.kind === "null") return 1;
        if (nb.kind === "null") return -1;

        if (na.kind === "number" && nb.kind === "number") {
          if (na.value === nb.value) return 0;
          return na.value > nb.value ? dir : -dir;
        }

        const cmp = collator.compare(String(na.value), String(nb.value));
        return cmp * dir;
      });

      return sorted;
    }, [columns, data, sort.direction, sort.key]);

    const visibleRows = useMemo(() => {
      if (!limit || limit <= 0) return rows;
      return rows.slice(0, limit);
    }, [limit, rows]);

    const handleHeaderClick = (col) => {
      if (!col || col.sortable === false) return;

      setSort((prev) => {
        if (prev.key !== col.id) return { key: col.id, direction: "asc" };
        return { key: col.id, direction: prev.direction === "asc" ? "desc" : "asc" };
      });
    };

    const renderSortIcon = (col) => {
      const isActive = sort.key === col.id;
      if (col.sortable === false) return null;
      if (!isActive) return <SortIcon $active={false}>↕</SortIcon>;
      return <SortIcon $active={true}>{sort.direction === "asc" ? "▲" : "▼"}</SortIcon>;
    };

    const colCount = Array.isArray(columns) ? columns.length : 0;

    const handleScroll = useCallback(
      (event) => {
        if (!onScrollReachEnd || isLoading) return;

        const el = event.currentTarget;
        const { scrollTop, scrollHeight, clientHeight } = el;

        if (scrollTop + clientHeight < scrollHeight - scrollReachThreshold) return;

        onScrollReachEnd();
      },
      [isLoading, onScrollReachEnd, scrollReachThreshold]
    );

    return (
      <OuterShell>
        <ScrollArea ref={ref} onScroll={onScrollReachEnd ? handleScroll : undefined}>
        <Table>
          <thead>
            <tr>
              {columns.map((col) => (
                <Th key={col.id} $width={col.width} $align={col.align}>
                  {col.sortable === false ? (
                    col.header
                  ) : (
                    <SortButton type="button" onClick={() => handleHeaderClick(col)}>
                      <span>{col.header}</span>
                      {renderSortIcon(col)}
                    </SortButton>
                  )}
                </Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <EmptyCell colSpan={Math.max(colCount, 1)}>{loadingText}</EmptyCell>
              </tr>
            ) : visibleRows.length === 0 ? (
              <tr>
                <EmptyCell colSpan={Math.max(colCount, 1)}>{emptyText}</EmptyCell>
              </tr>
            ) : (
              visibleRows.map((row, idx) => {
                const rowProps = getRowProps ? getRowProps(row, idx) : null;
                const cursor = onRowClick ? rowCursor : "default";
                return (
                  <tr
                    key={getRowId(row, idx)}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className={rowProps?.className}
                    style={{ cursor, ...(rowProps?.style || {}) }}
                  >
                    {columns.map((col) => {
                      const value = getByAccessor(row, col.accessor);
                      const content = col.render ? col.render(value, row) : value;
                      return (
                        <Td key={col.id} $width={col.width} $align={col.align}>
                          {content ?? "-"}
                        </Td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
        </ScrollArea>
      </OuterShell>
    );
  }
);

export default SortableTable;
